import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChartData, ChartOptions, ChartType, LegendItem, Chart as TypeChart } from "chart.js";
import Zoom from "chartjs-plugin-zoom";
import { Moment } from "moment";
import { Chart, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { Spin } from "antd";

import { ResolutionType } from "../../../../store/common_types";
import { IntegrationErrorChartItem } from "../../../../store/integration_errors/types";
import { minRangeWindowMap, resolutionUnitMap, useRangeLimit } from "../../../../utils/resolution";

export interface IntegrationErrorsChartProps {
  loading: boolean;
  items: IntegrationErrorChartItem[];
  rangeFrom: Moment;
  rangeTo: Moment;
  resolution: ResolutionType;
}

const integrationErrorsColor = [
  { key: "unknownErrors", yAxisID: "y", backgroundColor: "rgb(4, 201, 135)", borderColor: "rgba(4, 201, 135, .4)" },
  { key: "internalErrors", yAxisID: "y", backgroundColor: "rgb(184, 106, 61)", borderColor: "rgba(184, 106, 61, .4)" },
  { key: "parseErrors", yAxisID: "y", backgroundColor: "rgb(96, 57, 231)", borderColor: "rgba(96, 57, 231, .4)" },
  { key: "timeoutErrors", yAxisID: "y", backgroundColor: "rgb(224, 176, 255)", borderColor: "rgba(224, 176, 255, .6)" },
  { key: "partnerErrors", yAxisID: "y", backgroundColor: "rgb(222, 169, 55)", borderColor: "rgba(222, 169, 55, .4)" },
  { key: "requestErrors", yAxisID: "y", backgroundColor: "rgb(234, 131, 160)", borderColor: "rgba(234, 131, 160, .2)" },
  { key: "totalSearches", yAxisID: "y1", backgroundColor: "rgb(70, 170, 238)", borderColor: "rgba(70, 170, 238, .4)" },
];

export const IntegrationErrorsChart: React.FC<IntegrationErrorsChartProps> = (props) => {
  const { t } = useTranslation("integration_errors_page");

  const [data, setData] = useState<ChartData<"line", IntegrationErrorChartItem[]>>({ datasets: [] });
  const rangeLimit = useRangeLimit(props.rangeFrom, props.rangeTo, props.resolution);
  const [options, setOptions] = useState<ChartOptions<"line">>({});
  const chartRef = useRef<TypeChart<"line", IntegrationErrorChartItem[]>>();

  useEffect(() => {
    Chart.register(Zoom);
  }, []);

  useEffect(() => {
    setData({
      datasets: integrationErrorsColor.map(({ key, yAxisID, backgroundColor, borderColor }) => ({
        label: t(`chart.datasets.labels.${key}`),
        parsing: { yAxisKey: key, xAxisKey: "timestamp" },
        fill: false,
        data: props.items,
        yAxisID,
        backgroundColor,
        borderColor,
        hidden: !props.items.some((element: any) => element[key] > 0),
      })),
    });
  }, [t, props.items]);

  useEffect(() => {
    const minRange = minRangeWindowMap[props.resolution];
    const rangeDiff = rangeLimit.max - rangeLimit.min;

    setOptions({
      responsive: true,
      interaction: { intersect: false, mode: "index" },
      elements: { point: { radius: 0 } },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            filter: (item: LegendItem, chartData: ChartData): boolean => {
              const dataSet = chartData.datasets[item.datasetIndex];
              if (dataSet.parsing && dataSet.parsing.yAxisKey) {
                const field = dataSet.parsing.yAxisKey;
                return dataSet.data.some((record: any) => record[field] > 0);
              }
              return false;
            },
          },
        },
        tooltip: { position: "nearest" /* , filter: (e): boolean => e.parsed.y > 0 */ },
        zoom: {
          limits: { x: { ...rangeLimit, minRange } },
          zoom: { enabled: minRange < rangeDiff, mode: "x", threshold: 1 },
        },
      },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: t(`chart.legends.y`) }, ticks: { precision: 0 } },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          stacked: false,
          grid: { drawOnChartArea: false },
          title: { display: true, text: t("chart.legends.y1") },
          ticks: { precision: 0 },
        },
        x: {
          type: "time",
          time: {
            isoWeekday: 1,
            displayFormats: {
              minute: "YYYY-MM-DD HH:mm",
              hour: "YYYY-MM-DD HH:mm",
              day: "YYYY-MM-DD",
              month: "YYYY-MM",
            },
            ...resolutionUnitMap[props.resolution],
          },
          offset: false,
          ticks: { maxRotation: 0, align: "center" },
          ...rangeLimit,
        },
      },
    });
  }, [props.resolution, rangeLimit, t]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart !== undefined) {
      chart.options = options;
      chart.data = data;
      chart.update("none");
    }
  }, [data, options]);

  const chartComponent = useMemo(() => {
    return <Line ref={chartRef} data={{ datasets: [] }} height={75} type={"line" as ChartType} />;
  }, []);

  return <Spin spinning={props.loading}>{chartComponent}</Spin>;
};
